// R-131-FIN-DECISION-G — carbon pricing + RE100 compliance penalty overlay.

import type { ProjectIntent } from '../../types/siteRag';
import { mergePathway } from './presets/shared';
import { nameplateAnnualMwh } from './pathwayCashflows';
import { computePathwayLcoe } from './pathwayLcoe';
import type { Pathway } from './types';

/** Default internal carbon price when none is supplied ($/tCO2e). */
export const DEFAULT_CARBON_PRICE_USD_PER_T = 50;

/** Voluntary REC market rate — $/MWh (1 REC ≈ 1 MWh). */
export const DEFAULT_REC_MARKET_RATE_USD_PER_MWH = 8;

export interface PathwayCarbonAdjustOptions {
  /** Site carbon commitment from projectIntent (R-131-PP1-MATRIX). */
  carbonCommitment?: ProjectIntent['carbonCommitment'];
  /** When false, returns the base pathway with zero carbon attribution. */
  applyCarbonOverlay?: boolean;
  /** Internal carbon price override ($/tCO2e). */
  carbonPricePerTUsd?: number;
  /** REC compliance market rate ($/MWh). */
  recMarketRatePerMwhUsd?: number;
}

export interface PathwayCarbonCostBreakdown {
  annualTCo2e: number;
  carbonCostPerMwhUsd: number;
  internalCarbonPricePerMwhUsd: number;
  re100CompliancePenaltyPerMwhUsd: number;
}

export interface PathwayCarbonAdjustResult extends PathwayCarbonCostBreakdown {
  adjustedPathway: Pathway;
  /** Incremental nominal LCOE from carbon overlay ($/MWh). */
  lcoeCarbonAttributionUsdPerMwh: number;
}

function requiresRe100CompliancePenalty(
  commitment?: ProjectIntent['carbonCommitment'],
): boolean {
  return commitment === 're100' || commitment === '24-7-cfe';
}

/**
 * Compute per-MWh carbon costs without mutating the pathway.
 * Internal carbon price: tCO2e/MWh × $/tCO2.
 * RE100 / 24-7-CFE: REC purchase penalty when the pathway emits CO2.
 */
export function computePathwayCarbonCosts(
  pathway: Pathway,
  options: PathwayCarbonAdjustOptions = {},
): PathwayCarbonCostBreakdown {
  const annualMwh = nameplateAnnualMwh(pathway);
  const tCo2ePerMwh = pathway.carbon.tCo2ePerMwh;
  const annualTCo2e = tCo2ePerMwh * annualMwh;

  if (options.applyCarbonOverlay === false) {
    return {
      annualTCo2e,
      carbonCostPerMwhUsd: 0,
      internalCarbonPricePerMwhUsd: 0,
      re100CompliancePenaltyPerMwhUsd: 0,
    };
  }

  const carbonPricePerT =
    options.carbonPricePerTUsd ??
    pathway.carbon.carbonPricePerTUsd ??
    DEFAULT_CARBON_PRICE_USD_PER_T;

  const internalCarbonPricePerMwhUsd =
    tCo2ePerMwh > 0 ? tCo2ePerMwh * carbonPricePerT : 0;

  const re100CompliancePenaltyPerMwhUsd =
    requiresRe100CompliancePenalty(options.carbonCommitment) && tCo2ePerMwh > 0
      ? (options.recMarketRatePerMwhUsd ?? DEFAULT_REC_MARKET_RATE_USD_PER_MWH)
      : 0;

  return {
    annualTCo2e,
    carbonCostPerMwhUsd: internalCarbonPricePerMwhUsd + re100CompliancePenaltyPerMwhUsd,
    internalCarbonPricePerMwhUsd,
    re100CompliancePenaltyPerMwhUsd,
  };
}

/**
 * Apply carbon pricing and RE100 compliance penalties to a pathway.
 * Carbon costs are folded into `opex.variableOmPerMwhUsd`; LCOE attribution
 * is returned separately for UI breakdown.
 */
export function applyPathwayCarbonAdjust(
  pathway: Pathway,
  options: PathwayCarbonAdjustOptions = {},
): PathwayCarbonAdjustResult {
  const costs = computePathwayCarbonCosts(pathway, options);
  const applyOverlay = options.applyCarbonOverlay !== false;

  const carbonPricePerT =
    options.carbonPricePerTUsd ??
    pathway.carbon.carbonPricePerTUsd ??
    DEFAULT_CARBON_PRICE_USD_PER_T;

  const adjustedPathway =
    applyOverlay && costs.carbonCostPerMwhUsd > 0
      ? mergePathway(pathway, {
          opex: {
            ...pathway.opex,
            variableOmPerMwhUsd:
              pathway.opex.variableOmPerMwhUsd + costs.carbonCostPerMwhUsd,
          },
          carbon: {
            ...pathway.carbon,
            carbonPricePerTUsd: carbonPricePerT,
            re100Penalty: costs.re100CompliancePenaltyPerMwhUsd > 0,
          },
        })
      : pathway;

  const lcoeCarbonAttributionUsdPerMwh =
    applyOverlay && costs.carbonCostPerMwhUsd > 0
      ? computePathwayLcoe(adjustedPathway).lcoeNominalPerMwhUsd -
        computePathwayLcoe(pathway).lcoeNominalPerMwhUsd
      : 0;

  return {
    adjustedPathway,
    ...costs,
    lcoeCarbonAttributionUsdPerMwh,
  };
}
